import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StraightenIcon from "@mui/icons-material/Straighten";
import { EmptyState, LoadingState } from "../UI/States";
import {
  clearSlabStylesCache,
  getSlabStyleImage,
} from "../WindowDoor/utilities";

const FALLBACK_CORE_TYPES = ["H.C.", "S.C.", "Pine", "Bifold"];

const DEFAULT_WIDTHS = [
  "1'0\"",
  "1'2\"",
  "1'3\"",
  "1'4\"",
  "1'6\"",
  "1'8\"",
  "1'10\"",
  "2'0\"",
  "2'2\"",
  "2'4\"",
  "2'6\"",
  "2'8\"",
  "2'10\"",
  "3'0\"",
  "4'0\"",
  "5'0\"",
  "6'0\"",
];

const StatusText = ({ status }) =>
  status ? (
    <p
      role="status"
      className={`text-sm ${
        status.ok
          ? "text-emerald-700 dark:text-emerald-300"
          : "text-rose-700 dark:text-rose-300"
      }`}
    >
      {status.message}
    </p>
  ) : null;

const StyleRow = ({ style, coreTypes, onChange, onRemove, onUpload }) => {
  const fileInput = useRef(null);

  return (
    <tr>
      <td>
        <span className="app-light-surface inline-flex h-14 w-11 items-center justify-center rounded-lg border border-slate-200 bg-white p-0.5">
          <img
            src={getSlabStyleImage(style.image)}
            alt={style.name || "Slab style"}
            className="h-full w-full object-contain"
          />
        </span>
      </td>
      <td>
        <TextField
          size="small"
          placeholder="Style name"
          value={style.name}
          onChange={(e) => onChange({ ...style, name: e.target.value })}
          className="w-44"
          inputProps={{ "aria-label": "Style name" }}
        />
      </td>
      <td>
        <div className="flex flex-wrap items-center gap-1">
          {coreTypes.map((core) => (
            <label
              key={core}
              className="flex items-center whitespace-nowrap text-sm"
            >
              <Checkbox
                size="small"
                checked={style.cores.includes(core)}
                onChange={(e) =>
                  onChange({
                    ...style,
                    cores: e.target.checked
                      ? [...style.cores, core]
                      : style.cores.filter((item) => item !== core),
                  })
                }
              />
              {core}
            </label>
          ))}
        </div>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <TextField
            size="small"
            placeholder="image.png"
            value={style.image}
            onChange={(e) => onChange({ ...style, image: e.target.value })}
            className="w-44"
            inputProps={{ "aria-label": "Image file name" }}
          />
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                onUpload(e.target.files[0]);
              }
              e.target.value = "";
            }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => fileInput.current?.click()}
          >
            Upload
          </Button>
        </div>
      </td>
      <td className="text-right">
        <Tooltip title="Remove style">
          <IconButton
            size="small"
            color="error"
            onClick={onRemove}
            aria-label={`Remove ${style.name || "style"}`}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </td>
    </tr>
  );
};

const SlabStylesManager = ({ userId }) => {
  const [styles, setStyles] = useState([]);
  const [coreTypes, setCoreTypes] = useState(FALLBACK_CORE_TYPES);
  const [loading, setLoading] = useState(true);
  const [savingStyles, setSavingStyles] = useState(false);
  const [stylesStatus, setStylesStatus] = useState(null);

  const [selectedStyle, setSelectedStyle] = useState("");
  const [selectedCore, setSelectedCore] = useState("");
  const [rows, setRows] = useState([]);
  const [savingRows, setSavingRows] = useState(false);
  const [rowsStatus, setRowsStatus] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch("/api/doors/kits/getSlabStyles");
        const data = await response.json();
        setStyles(data.styles || []);
        if (data.coreTypes?.length) {
          setCoreTypes(data.coreTypes);
        }
      } catch (error) {
        setStylesStatus({ ok: false, message: "Unable to load slab styles." });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const fetchRows = async (slabStyle, coreType) => {
    setRowsStatus(null);
    if (!slabStyle || !coreType) {
      setRows([]);
      return;
    }

    try {
      const response = await fetch(
        `/api/doors/kits/getSlabOptions?slabStyle=${encodeURIComponent(
          slabStyle
        )}&coreType=${encodeURIComponent(coreType)}`
      );
      const data = await response.json();

      // A missing document 500s — treat it as "no widths configured yet".
      if (!Array.isArray(data.options)) {
        setRows([]);
        return;
      }

      setRows(
        data.options.map((option) => ({
          width: option.width || "",
          productCode: option.productCode ? String(option.productCode) : "NS",
        }))
      );
    } catch (error) {
      setRows([]);
    }
  };

  const handleStyleChange = (value) => {
    setSelectedStyle(value);
    const style = styles.find((item) => item.name === value);
    // Keep the core only if it still applies to the newly chosen style.
    const core =
      style && style.cores.includes(selectedCore) ? selectedCore : "";
    setSelectedCore(core);
    fetchRows(value, core);
  };

  const handleCoreChange = (value) => {
    setSelectedCore(value);
    fetchRows(selectedStyle, value);
  };

  const updateRow = (index, patch) =>
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );

  const handleSaveStyles = async () => {
    for (const style of styles) {
      if (!style.name.trim()) {
        setStylesStatus({ ok: false, message: "Every style needs a name." });
        return;
      }
      if (!style.cores.length) {
        setStylesStatus({
          ok: false,
          message: `"${style.name}" needs at least one core type.`,
        });
        return;
      }
    }
    const names = styles.map((style) => style.name.trim());
    if (new Set(names).size !== names.length) {
      setStylesStatus({ ok: false, message: "Style names must be unique." });
      return;
    }

    setSavingStyles(true);
    setStylesStatus(null);
    try {
      const response = await fetch("/api/doors/kits/saveSlabStyles", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-id": userId || "",
        },
        body: JSON.stringify({ styles }),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        setStylesStatus({ ok: false, message: data.message || "Save failed" });
      } else {
        clearSlabStylesCache();
        setStylesStatus({
          ok: true,
          message: "Saved — the configurator now shows this list.",
        });
      }
    } catch (error) {
      setStylesStatus({ ok: false, message: "Save failed" });
    } finally {
      setSavingStyles(false);
    }
  };

  const handleUpload = async (index, file) => {
    const body = new FormData();
    body.append("file", file);
    try {
      const response = await fetch("/api/doors/kits/image", {
        method: "POST",
        body,
      });
      const data = await response.json();
      if (data.success) {
        setStyles((prev) =>
          prev.map((style, i) =>
            i === index ? { ...style, image: file.name } : style
          )
        );
      } else {
        setStylesStatus({ ok: false, message: "Image upload failed" });
      }
    } catch (error) {
      setStylesStatus({ ok: false, message: "Image upload failed" });
    }
  };

  const handleSaveRows = async () => {
    const widths = rows.map((row) => row.width.trim());
    if (widths.some((width) => !width)) {
      setRowsStatus({ ok: false, message: "Every row needs a width." });
      return;
    }
    if (new Set(widths).size !== widths.length) {
      setRowsStatus({ ok: false, message: "Widths must be unique." });
      return;
    }
    const badRow = rows.find(
      (row) =>
        row.productCode.trim() &&
        row.productCode.trim().toUpperCase() !== "NS" &&
        !/^\d+$/.test(row.productCode.trim())
    );
    if (badRow) {
      setRowsStatus({
        ok: false,
        message: `"${badRow.productCode}" is not a valid product code — use digits or NS.`,
      });
      return;
    }

    setSavingRows(true);
    setRowsStatus(null);
    try {
      const response = await fetch("/api/doors/kits/saveSlabOptions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-id": userId || "",
        },
        body: JSON.stringify({
          slabStyle: selectedStyle,
          coreType: selectedCore,
          options: rows.map((row) => {
            const code = row.productCode.trim().toUpperCase();
            return {
              width: row.width.trim(),
              productCode: code && code !== "NS" ? code : "NS",
            };
          }),
        }),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        setRowsStatus({ ok: false, message: data.message || "Save failed" });
      } else {
        setRowsStatus({ ok: true, message: "Widths saved." });
      }
    } catch (error) {
      setRowsStatus({ ok: false, message: "Save failed" });
    } finally {
      setSavingRows(false);
    }
  };

  const selectedStyleObj = styles.find((item) => item.name === selectedStyle);

  if (loading) {
    return <LoadingState label="Loading slab styles…" />;
  }

  return (
    <>
      <div className="app-card mb-6 overflow-hidden rounded-2xl border">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b app-divider px-4 py-3 sm:px-5">
          <h2 className="text-lg font-semibold">Slab Styles</h2>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              setStyles((prev) => [...prev, { name: "", cores: [], image: "" }])
            }
          >
            Add Style
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="app-table">
            <thead>
              <tr>
                {["", "Name", "Core Types", "Image", ""].map(
                  (header, index) => (
                    <th key={index} className="uppercase tracking-wider">
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {styles.map((style, index) => (
                <StyleRow
                  key={index}
                  style={style}
                  coreTypes={coreTypes}
                  onChange={(next) =>
                    setStyles((prev) =>
                      prev.map((item, i) => (i === index ? next : item))
                    )
                  }
                  onRemove={() =>
                    setStyles((prev) => prev.filter((_, i) => i !== index))
                  }
                  onUpload={(file) => handleUpload(index, file)}
                />
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t app-divider px-4 py-3 sm:px-5">
          <Button
            variant="contained"
            onClick={handleSaveStyles}
            disabled={savingStyles}
          >
            {savingStyles ? "Saving…" : "Save Styles"}
          </Button>
          <StatusText status={stylesStatus} />
        </div>
      </div>

      <div className="app-card rounded-2xl border p-4 sm:p-5">
        <h2 className="mb-1 text-lg font-semibold">Widths &amp; Product Codes</h2>
        <p className="app-muted mb-4 text-sm">
          Widths listed here become the width dropdown for that style and core.
          Rows with a product code are quotable; NS rows show as "Non Stock
          (Call For Pricing)".
        </p>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row">
          <select
            className="app-input rounded-xl border px-3 py-2 text-sm disabled:opacity-50 sm:w-60"
            value={selectedStyle}
            onChange={(e) => handleStyleChange(e.target.value)}
            aria-label="Slab style"
          >
            <option value="">Select Slab Style</option>
            {styles.map((style) => (
              <option key={style.name} value={style.name}>
                {style.name}
              </option>
            ))}
          </select>
          <select
            className="app-input rounded-xl border px-3 py-2 text-sm disabled:opacity-50 sm:w-60"
            value={selectedCore}
            onChange={(e) => handleCoreChange(e.target.value)}
            disabled={!selectedStyle}
            aria-label="Core type"
          >
            <option value="">Select Core Type</option>
            {(selectedStyleObj?.cores || coreTypes).map((core) => (
              <option key={core} value={core}>
                {core}
              </option>
            ))}
          </select>
        </div>

        {selectedStyle && selectedCore ? (
          <>
            {rows.length === 0 && (
              <div className="app-surface mb-4 rounded-xl border px-4 py-3">
                <p className="app-muted mb-2 text-sm">
                  No widths configured for this combination yet.
                </p>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setRows(
                      DEFAULT_WIDTHS.map((width) => ({
                        width,
                        productCode: "NS",
                      }))
                    )
                  }
                >
                  Start From Default Widths
                </Button>
              </div>
            )}

            {rows.length > 0 && (
              <div className="app-surface mb-4 overflow-hidden rounded-xl border sm:inline-block">
                <div className="overflow-x-auto">
                  <table className="app-table">
                    <thead>
                      <tr>
                        {["Width", "Product Code", ""].map((header, index) => (
                          <th key={index} className="uppercase tracking-wider">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={index}>
                          <td className="!py-1.5">
                            <TextField
                              size="small"
                              placeholder={`2'6"`}
                              value={row.width}
                              onChange={(e) =>
                                updateRow(index, { width: e.target.value })
                              }
                              className="w-24"
                              inputProps={{ "aria-label": "Width" }}
                            />
                          </td>
                          <td className="!py-1.5">
                            <TextField
                              size="small"
                              placeholder="NS"
                              value={row.productCode}
                              onChange={(e) =>
                                updateRow(index, {
                                  productCode: e.target.value,
                                })
                              }
                              className="w-32"
                              inputProps={{ "aria-label": "Product code" }}
                            />
                          </td>
                          <td className="!py-1.5 text-right">
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={`Remove width ${row.width || index + 1}`}
                              onClick={() =>
                                setRows((prev) =>
                                  prev.filter((_, i) => i !== index)
                                )
                              }
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setRows((prev) => [...prev, { width: "", productCode: "NS" }])
                }
              >
                Add Width
              </Button>
              {rows.length > 0 && (
                <Button
                  variant="contained"
                  onClick={handleSaveRows}
                  disabled={savingRows}
                >
                  {savingRows ? "Saving…" : "Save Widths"}
                </Button>
              )}
              <StatusText status={rowsStatus} />
            </div>
          </>
        ) : (
          <div className="app-surface rounded-xl border">
            <EmptyState
              compact
              icon={StraightenIcon}
              description="Select a slab style and core type to edit its widths."
            />
          </div>
        )}
      </div>
    </>
  );
};

export default SlabStylesManager;
